// ./src/components/features/Artists/ArtistEditDrawer/ArtistEditDrawer.tsx
//
// Create/edit drawer for an artist (account-level library entry — a bookable act:
// a band, DJ, soloist, speaker…). Self-loading in edit mode via recordId;
// context-or-props resolution; no success toast (the page highlight is the
// confirmation). Fields: name, kind, photo, summary, description, genre/category,
// links, agent contact, booking defaults, rider, notes, and a repeatable members
// editor (the full desired set is sent and diffed on id server-side). Mirrors
// InventoryItemEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, Textarea, Group, Divider, Skeleton, Input, Button, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import { StackedModal } from "@em/ui";

import {
  Artist,
  ArtistInput,
  ArtistMember,
  ArtistService,
  ARTIST_KIND,
  ARTIST_KIND_VALUES,
  ARTIST_KIND_LABELS,
  ARTIST_GROUP_KINDS,
  ARTIST_DEAL_TYPE,
  ARTIST_DEAL_TYPE_VALUES,
  ARTIST_DEAL_TYPE_LABELS,
} from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalArtistsContext } from "@/contexts";
import {
  AppDrawer,
  AppIcon,
  CategorySelect,
  CurrencySelector,
  NumberField,
  MediaImageField,
  MediaImageRef,
  SelectField,
} from "@/components/generic";
import { MoneyInput } from "@/components/features/Budget/MoneyInput/MoneyInput";
import { ArtistMembersEditor } from "../ArtistMembersEditor/ArtistMembersEditor";

// RichTextField (Tiptap WYSIWYG — headings, text/bg colour, alignment, lists,
// links, tables) pulls in a heavy editor bundle — lazy-load it by path so it
// doesn't leak into the shared bundle (it's not re-exported from the generic barrel).
const RichTextField = React.lazy(() =>
  import("@/components/generic/RichTextField/RichTextField").then((m) => ({ default: m.RichTextField }))
);

export interface ArtistEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  onCreated?: (item: Artist) => void;
  onUpdated?: (item: Artist) => void;
}

interface FormData {
  name: string;
  kind: string | null;
  categoryId: string | null;
  summary: string;
  description: string;
  mainImage: MediaImageRef | null;
  website: string;
  spotifyUrl: string;
  youtubeUrl: string;
  socialUrl1: string;
  socialUrl2: string;
  socialUrl3: string;
  agentName: string;
  agentCompany: string;
  agentEmail: string;
  agentPhone: string;
  setLengthMin: number | null;
  dealType: string | null;
  defaultAmount: number | null;
  currencyCode: string | null;
  rider: string;
  notes: string;
  members: ArtistMember[];
  status: number;
}

// ⚠ Only list a field that some control here can actually SHOW. `getFormErrors` puts a KNOWN name in
// `fields` and NEVER in `genericErrors`, so a listed field with nowhere to land SWALLOWS its message and
// the save fails with nothing on screen — strictly worse than not listing it. One key is deliberately
// absent: `status` (this drawer has no status control — it only submits the value). Left off, its message
// falls through to the footer alert prefixed with the field name, which is visible.
const KNOWN_FORM_FIELDS = [
  "mainImageUploadedFileId",
  "name",
  "kind",
  "categoryId",
  "summary",
  "description",
  "website",
  "spotifyUrl",
  "youtubeUrl",
  "socialUrl1",
  "socialUrl2",
  "socialUrl3",
  "agentName",
  "agentCompany",
  "agentEmail",
  "agentPhone",
  "setLengthMin",
  "dealType",
  "defaultAmount",
  "currencyCode",
  "rider",
  "notes",
  "members",
];

const getInitialFormData = (): FormData => ({
  name: "",
  kind: ARTIST_KIND.BAND,
  categoryId: null,
  summary: "",
  description: "",
  mainImage: null,
  website: "",
  spotifyUrl: "",
  youtubeUrl: "",
  socialUrl1: "",
  socialUrl2: "",
  socialUrl3: "",
  agentName: "",
  agentCompany: "",
  agentEmail: "",
  agentPhone: "",
  setLengthMin: null,
  dealType: ARTIST_DEAL_TYPE.NONE,
  defaultAmount: null,
  currencyCode: null,
  rider: "",
  notes: "",
  members: [],
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Artist): FormData => ({
  name: record.name || "",
  kind: record.kind || ARTIST_KIND.BAND,
  categoryId: record.categoryId || null,
  summary: record.summary || "",
  description: record.description || "",
  // The BE resolves the promo photo via federation, so the preview renders on
  // edit-load. Keep the id even if the url is missing so save never drops it.
  mainImage: record.mainImageUploadedFileId
    ? {
        uploadedFileId: record.mainImageUploadedFileId,
        url: record.mainImageUploadedFile?.fileUrl ?? record.mainImageUploadedFile?.thumbnailUrl ?? null,
      }
    : null,
  website: record.website || "",
  spotifyUrl: record.spotifyUrl || "",
  youtubeUrl: record.youtubeUrl || "",
  socialUrl1: record.socialUrl1 || "",
  socialUrl2: record.socialUrl2 || "",
  socialUrl3: record.socialUrl3 || "",
  agentName: record.agentName || "",
  agentCompany: record.agentCompany || "",
  agentEmail: record.agentEmail || "",
  agentPhone: record.agentPhone || "",
  setLengthMin: record.setLengthMin ?? null,
  dealType: record.dealType || ARTIST_DEAL_TYPE.NONE,
  defaultAmount: record.defaultAmount ?? null,
  currencyCode: record.currencyCode || null,
  rider: record.rider || "",
  notes: record.notes || "",
  members: (record.members ?? []).map((m) => ({
    id: m.id,
    personId: m.personId ?? null,
    nameTitle: m.nameTitle ?? null,
    // Raw OVERRIDE name (blank when linked = use the contact's current name).
    firstName: m.firstName || "",
    lastName: m.lastName || "",
    email: m.email ?? null,
    phone: m.phone ?? null,
    avatarUploadedFileId: m.avatarUploadedFileId ?? null,
    // Effective values (override ?? linked contact's CURRENT) — the editor shows
    // these as the linked contact's live name so a rename is reflected immediately.
    displayFirstName: m.displayFirstName ?? null,
    displayLastName: m.displayLastName ?? null,
    displayEmail: m.displayEmail ?? null,
    displayPhone: m.displayPhone ?? null,
    displayAvatarUploadedFileId: m.displayAvatarUploadedFileId ?? null,
    memberRole: m.memberRole || "",
    isCore: m.isCore ?? true,
  })),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const ArtistEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const ArtistEditDrawer: React.FC<ArtistEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["artists", "common"]);

  const ctx = useOptionalArtistsContext();
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading (via the service directly, so it works with or without a page list) ──
  const [record, setRecord] = useState<Artist | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      ArtistService.get(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Artist);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  // Gate ⌘S while the member picker's "create contact" sub-drawer is open.
  const [personCreateOpen, setPersonCreateOpen] = useState(false);
  const [riderExpanded, setRiderExpanded] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    let newFormData: FormData;

    if (record) {
      newFormData = fromRecord(record);
    } else {
      newFormData = {
        ...getInitialFormData(),
        name: (initialName || "").trim(),
      };
    }

    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const kindOptions = ARTIST_KIND_VALUES.map((k) => ({ value: k, label: ARTIST_KIND_LABELS[k] ?? k }));
  const dealTypeOptions = ARTIST_DEAL_TYPE_VALUES.map((d) => ({ value: d, label: ARTIST_DEAL_TYPE_LABELS[d] ?? d }));

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    // Send only members that have a name or a linked person; keep ids so the server
    // diffs in place.
    const members = formData.members
      .filter((m) => (m.firstName || "").trim() || (m.lastName || "").trim() || m.personId)
      .map((m) => {
        const linked = !!m.personId;
        // A linked member's identity comes LIVE from the contact — never store a
        // name/contact override (this also heals any legacy copied name). An
        // unlinked member carries its typed name.
        return {
          id: m.id,
          personId: m.personId || null,
          nameTitle: linked ? null : (m.nameTitle || "").trim() || null,
          firstName: linked ? null : (m.firstName || "").trim() || null,
          lastName: linked ? null : (m.lastName || "").trim() || null,
          email: linked ? null : (m.email || "").trim() || null,
          phone: linked ? null : (m.phone || "").trim() || null,
          avatarUploadedFileId: linked ? null : m.avatarUploadedFileId || null,
          memberRole: (m.memberRole || "").trim() || null,
          isCore: m.isCore ?? false,
        };
      });

    const isGroup =
      (formData.kind ? ARTIST_GROUP_KINDS.includes(formData.kind) : false) || members.length > 1;

    const submitData: ArtistInput = {
      name: formData.name.trim() || null,
      kind: formData.kind,
      isGroup,
      categoryId: formData.categoryId,
      summary: formData.summary.trim() || null,
      description: formData.description.trim() || null,
      mainImageUploadedFileId: formData.mainImage?.uploadedFileId ?? null,
      website: formData.website.trim() || null,
      spotifyUrl: formData.spotifyUrl.trim() || null,
      youtubeUrl: formData.youtubeUrl.trim() || null,
      socialUrl1: formData.socialUrl1.trim() || null,
      socialUrl2: formData.socialUrl2.trim() || null,
      socialUrl3: formData.socialUrl3.trim() || null,
      agentName: formData.agentName.trim() || null,
      agentCompany: formData.agentCompany.trim() || null,
      agentEmail: formData.agentEmail.trim() || null,
      agentPhone: formData.agentPhone.trim() || null,
      setLengthMin: formData.setLengthMin,
      dealType: formData.dealType,
      defaultAmount: formData.defaultAmount,
      currencyCode: formData.currencyCode,
      rider: formData.rider.trim() || null,
      notes: formData.notes.trim() || null,
      status: formData.status,
      members,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Artist[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord && !personCreateOpen && !riderExpanded,
    enabled: opened && !personCreateOpen && !riderExpanded,
  });

  const drawerTitle = isEditMode
    ? t("artists:form.editTitle", { defaultValue: "Edit artist" })
    : t("artists:form.addTitle", { defaultValue: "Add artist" });

  return (
    <>
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <ArtistEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("artists:form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("artists:form.fields.name.placeholder", { defaultValue: "e.g. The Night Owls" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            required
            data-autofocus
            size="md"
          />

          <Group grow align="flex-start">
            <SelectField
              label={t("artists:form.fields.kind.label", { defaultValue: "Kind" })}
              data={kindOptions}
              value={formData.kind}
              onChange={(_name, v) => setField("kind", v)}
              error={fieldErrors.kind}
              size="md"
            />
            <CategorySelect
              group="artist"
              exactGroup
              label={t("artists:form.fields.category.label", { defaultValue: "Genre" })}
              value={formData.categoryId}
              onChange={(id) => setField("categoryId", id)}
              error={fieldErrors.categoryId}
              clearable
              size="md"
            />
          </Group>

          <MediaImageField
            intent="avatar"
            allowUrl={false}
            value={formData.mainImage}
            onChange={(img) => setField("mainImage", img)}
            label={t("artists:form.fields.photo.label", { defaultValue: "Photo" })}
            description={t("artists:form.fields.photo.description", {
              defaultValue: "A promo photo shown wherever this act appears.",
            })}
            addLabel={t("artists:form.fields.photo.add", { defaultValue: "Add photo" })}
            error={fieldErrors.mainImageUploadedFileId}
            disabled={isSubmitting}
          />

          <TextInput
            label={t("artists:form.fields.summary.label", { defaultValue: "Summary" })}
            placeholder={t("artists:form.fields.summary.placeholder", { defaultValue: "One-line description" })}
            value={formData.summary}
            onChange={(e) => setField("summary", e.currentTarget.value)}
            error={fieldErrors.summary}
            size="md"
          />

          <Textarea
            label={t("artists:form.fields.description.label", { defaultValue: "Description" })}
            placeholder={t("artists:form.fields.description.placeholder", { defaultValue: "Optional" })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />

          <Divider
            label={t("artists:form.sections.members", { defaultValue: "Members" })}
            labelPosition="left"
          />

          <ArtistMembersEditor
            value={formData.members}
            onChange={(members) => setField("members", members)}
            error={fieldErrors.members}
            onCreateOpenChange={setPersonCreateOpen}
            disabled={isSubmitting}
          />

          <Divider
            label={t("artists:form.sections.links", { defaultValue: "Links" })}
            labelPosition="left"
          />

          <Group grow align="flex-start">
            <TextInput
              label={t("artists:form.fields.website.label", { defaultValue: "Website" })}
              placeholder="https://"
              value={formData.website}
              onChange={(e) => setField("website", e.currentTarget.value)}
              error={fieldErrors.website}
              size="md"
            />
            <TextInput
              label={t("artists:form.fields.spotifyUrl.label", { defaultValue: "Spotify" })}
              placeholder="https://open.spotify.com/…"
              value={formData.spotifyUrl}
              onChange={(e) => setField("spotifyUrl", e.currentTarget.value)}
              error={fieldErrors.spotifyUrl}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t("artists:form.fields.youtubeUrl.label", { defaultValue: "YouTube" })}
              placeholder="https://youtube.com/…"
              value={formData.youtubeUrl}
              onChange={(e) => setField("youtubeUrl", e.currentTarget.value)}
              error={fieldErrors.youtubeUrl}
              size="md"
            />
            <TextInput
              label={t("artists:form.fields.socialUrl1.label", { defaultValue: "Social link 1" })}
              placeholder="https://"
              value={formData.socialUrl1}
              onChange={(e) => setField("socialUrl1", e.currentTarget.value)}
              error={fieldErrors.socialUrl1}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t("artists:form.fields.socialUrl2.label", { defaultValue: "Social link 2" })}
              placeholder="https://"
              value={formData.socialUrl2}
              onChange={(e) => setField("socialUrl2", e.currentTarget.value)}
              error={fieldErrors.socialUrl2}
              size="md"
            />
            <TextInput
              label={t("artists:form.fields.socialUrl3.label", { defaultValue: "Social link 3" })}
              placeholder="https://"
              value={formData.socialUrl3}
              onChange={(e) => setField("socialUrl3", e.currentTarget.value)}
              error={fieldErrors.socialUrl3}
              size="md"
            />
          </Group>

          <Divider
            label={t("artists:form.sections.agent", { defaultValue: "Agent / booking contact" })}
            labelPosition="left"
          />

          <Group grow align="flex-start">
            <TextInput
              label={t("artists:form.fields.agentName.label", { defaultValue: "Agent name" })}
              value={formData.agentName}
              onChange={(e) => setField("agentName", e.currentTarget.value)}
              error={fieldErrors.agentName}
              size="md"
            />
            <TextInput
              label={t("artists:form.fields.agentCompany.label", { defaultValue: "Agency / company" })}
              value={formData.agentCompany}
              onChange={(e) => setField("agentCompany", e.currentTarget.value)}
              error={fieldErrors.agentCompany}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t("artists:form.fields.agentEmail.label", { defaultValue: "Agent email" })}
              placeholder="agent@example.com"
              value={formData.agentEmail}
              onChange={(e) => setField("agentEmail", e.currentTarget.value)}
              error={fieldErrors.agentEmail}
              size="md"
            />
            <TextInput
              label={t("artists:form.fields.agentPhone.label", { defaultValue: "Agent phone" })}
              value={formData.agentPhone}
              onChange={(e) => setField("agentPhone", e.currentTarget.value)}
              error={fieldErrors.agentPhone}
              size="md"
            />
          </Group>

          <Divider
            label={t("artists:form.sections.booking", { defaultValue: "Booking defaults" })}
            labelPosition="left"
          />

          <Group grow align="flex-start">
            <NumberField
              label={t("artists:form.fields.setLengthMin.label", { defaultValue: "Set length (minutes)" })}
              placeholder={t("artists:form.fields.setLengthMin.placeholder", { defaultValue: "Optional" })}
              value={formData.setLengthMin}
              onChange={(_n, v) => setField("setLengthMin", v)}
              min={0}
              allowNegative={false}
              error={fieldErrors.setLengthMin}
              size="md"
            />
            <SelectField
              label={t("artists:form.fields.dealType.label", { defaultValue: "Deal type" })}
              data={dealTypeOptions}
              value={formData.dealType}
              onChange={(_name, v) => setField("dealType", v)}
              error={fieldErrors.dealType}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <MoneyInput
              label={t("artists:form.fields.defaultAmount.label", { defaultValue: "Default fee" })}
              value={formData.defaultAmount}
              onChange={(v) => setField("defaultAmount", v)}
              currency={formData.currencyCode}
              error={fieldErrors.defaultAmount}
            />
            <CurrencySelector
              label={t("artists:form.fields.currency.label", { defaultValue: "Currency" })}
              value={formData.currencyCode}
              onChange={(_name, value) => setField("currencyCode", value)}
              error={fieldErrors.currencyCode}
              isoOnly
              withinPortal
              size="md"
            />
          </Group>

          <Input.Wrapper
            label={t("artists:form.fields.rider.label", { defaultValue: "Rider" })}
            description={t("artists:form.fields.rider.description", {
              defaultValue: "Default technical + hospitality requirements.",
            })}
            error={fieldErrors.rider}
          >
            <Stack gap={6} mt={4}>
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<AppIcon iconName="Maximize" size={14} />}
                  onClick={() => setRiderExpanded(true)}
                >
                  {t("artists:form.fields.rider.expand", { defaultValue: "Expand" })}
                </Button>
              </Group>
              <Suspense fallback={<Skeleton height={220} radius="sm" />}>
                <RichTextField
                  value={formData.rider}
                  onChange={(html) => setField("rider", html)}
                  placeholder={t("artists:form.fields.rider.placeholder", {
                    defaultValue: "Technical / hospitality requirements…",
                  })}
                  minHeight={220}
                  allowMedia={false}
                />
              </Suspense>
            </Stack>
          </Input.Wrapper>

          <Textarea
            label={t("artists:form.fields.notes.label", { defaultValue: "Notes" })}
            placeholder={t("artists:form.fields.notes.placeholder", { defaultValue: "Internal notes…" })}
            value={formData.notes}
            onChange={(e) => setField("notes", e.currentTarget.value)}
            error={fieldErrors.notes}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />
        </Stack>
      )}
    </AppDrawer>

    {/* The rider, full screen — an EDITOR view of the drawer's own field, not a dialog (ROADMAP #120), so it is the house
        full-screen editor: a StackedModal on the overlay stack (Esc closes only it; the drawer's shortcuts stand aside),
        as NoteEditDrawer and PolicyTextEditor do. */}
    <StackedModal
      opened={riderExpanded}
      onClose={() => setRiderExpanded(false)}
      fullScreen
      radius={0}
      transitionProps={{ transition: "fade", duration: 150 }}
      title={t("artists:form.fields.rider.label", { defaultValue: "Rider" })}
      styles={{
        content: { height: "100dvh", display: "flex", flexDirection: "column" },
        body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-md)" },
      }}
    >
      <Text size="md" c="dimmed">
        {t("artists:form.fields.rider.description", {
          defaultValue: "Default technical + hospitality requirements.",
        })}
      </Text>
      <Box style={{ flex: 1, minHeight: 0 }}>
        <Suspense fallback={<Skeleton height="100%" radius="sm" />}>
          <RichTextField
            value={formData.rider}
            onChange={(html) => setField("rider", html)}
            placeholder={t("artists:form.fields.rider.placeholder", {
              defaultValue: "Technical / hospitality requirements…",
            })}
            fill
            allowMedia={false}
            autofocus
          />
        </Suspense>
      </Box>
    </StackedModal>
    </>
  );
};

export default ArtistEditDrawer;
